import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import type { DrillDownContext, InventoryBuyer, SimilarProductBuyers } from '../../types/inventory';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    item: DrillDownContext;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatEUR(num: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
}

function formatNumber(num: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
}

type SortKey = 'CustomerName' | 'SalesManagerName' | 'BusinessUnitCode' | 'CountryISO3' |
    'AbcClassification' | 'LastSaleDate' | 'Revenue12m' | 'RevenueAllTime' | 'TotalQuantity' | 'NrOfOrders';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, align }: {
    label: string;
    sortKey: SortKey;
    currentKey: SortKey;
    currentDir: SortDir;
    onSort: (key: SortKey) => void;
    align?: 'left' | 'right';
}) {
    const isActive = currentKey === sortKey;
    return (
        <th
            className={`sticky top-0 px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 bg-white ${
                align === 'right' ? 'text-right' : 'text-left'
            }`}
            onClick={() => onSort(sortKey)}
        >
            <span className="inline-flex items-center gap-0.5">
                {label}
                <span className="inline-flex flex-col -space-y-1">
                    <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                </span>
            </span>
        </th>
    );
}

function AbcBadge({ classification }: { classification: string | null }) {
    if (!classification) return <span className="text-gray-400">-</span>;
    const colors: Record<string, string> = {
        'A': 'bg-green-100 text-green-700',
        'B': 'bg-blue-100 text-blue-700',
        'C': 'bg-yellow-100 text-yellow-700',
        'D': 'bg-orange-100 text-orange-700',
    };
    return (
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${colors[classification] || 'bg-gray-100 text-gray-700'}`}>
            {classification}
        </span>
    );
}

function sortBuyers(buyers: InventoryBuyer[], sortKey: SortKey, sortDir: SortDir): InventoryBuyer[] {
    return [...buyers].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number'
            ? aVal - (bVal as number)
            : String(aVal).localeCompare(String(bVal));
        return sortDir === 'asc' ? cmp : -cmp;
    });
}

function BuyerTable({ buyers, sortKey, sortDir, onSort }: {
    buyers: InventoryBuyer[];
    sortKey: SortKey;
    sortDir: SortDir;
    onSort: (key: SortKey) => void;
}) {
    const sorted = useMemo(() => sortBuyers(buyers, sortKey, sortDir), [buyers, sortKey, sortDir]);

    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="border-b border-gray-200">
                    <SortHeader label="Customer" sortKey="CustomerName" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
                    <SortHeader label="Sales Manager" sortKey="SalesManagerName" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
                    <SortHeader label="BU" sortKey="BusinessUnitCode" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
                    <SortHeader label="Country" sortKey="CountryISO3" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
                    <SortHeader label="ABC" sortKey="AbcClassification" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
                    <SortHeader label="Last Sale" sortKey="LastSaleDate" currentKey={sortKey} currentDir={sortDir} onSort={onSort} />
                    <SortHeader label="Rev. 12m (EUR)" sortKey="Revenue12m" currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="right" />
                    <SortHeader label="Rev. All (EUR)" sortKey="RevenueAllTime" currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="right" />
                    <SortHeader label="Qty" sortKey="TotalQuantity" currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="right" />
                    <SortHeader label="Orders" sortKey="NrOfOrders" currentKey={sortKey} currentDir={sortDir} onSort={onSort} align="right" />
                </tr>
            </thead>
            <tbody>
                {sorted.map(b => (
                    <tr key={b.CustomerID} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="max-w-[180px] truncate px-3 py-2 text-gray-900">{b.CustomerName}</td>
                        <td className="max-w-[140px] truncate px-3 py-2 text-gray-600">{b.SalesManagerName || '-'}</td>
                        <td className="px-3 py-2 text-gray-600">{b.BusinessUnitCode || '-'}</td>
                        <td className="px-3 py-2 text-gray-600">{b.CountryISO3 || '-'}</td>
                        <td className="px-3 py-2"><AbcBadge classification={b.AbcClassification} /></td>
                        <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(b.LastSaleDate)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{formatEUR(b.Revenue12m)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{formatEUR(b.RevenueAllTime)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{formatNumber(b.TotalQuantity)}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{formatNumber(b.NrOfOrders)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export function BuyersModal({ isOpen, onClose, item }: Props) {
    const [buyers, setBuyers] = useState<InventoryBuyer[]>([]);
    const [similarProducts, setSimilarProducts] = useState<SimilarProductBuyers[]>([]);
    const [productInfo, setProductInfo] = useState<{ productName: string; productCode: string; companyCode: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('LastSaleDate');
    const [sortDir, setSortDir] = useState<SortDir>('desc');

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);

        inventoryApi.getBuyers(item.ItemID)
            .then(data => {
                setBuyers(data.buyers);
                setSimilarProducts(data.similarProducts);
                setProductInfo({ productName: data.productName, productCode: data.productCode, companyCode: data.companyCode });
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [isOpen, item.ItemID]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'LastSaleDate' || key === 'Revenue12m' || key === 'RevenueAllTime' ? 'desc' : 'asc');
        }
    };

    if (!isOpen) return null;

    const totalRevenue12m = buyers.reduce((s, b) => s + b.Revenue12m, 0);
    const totalRevenueAllTime = buyers.reduce((s, b) => s + b.RevenueAllTime, 0);

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* Modal */}
            <div className="relative z-10 mx-4 flex max-h-[85vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white px-4 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Buyers
                        </h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            {productInfo?.productName || item.ProductName} ({productInfo?.productCode || item.ProductCode})
                            {' '}&middot; {productInfo?.companyCode || item.CompanyCode}
                        </p>
                    </div>
                    <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Summary */}
                {!loading && !error && buyers.length > 0 && (
                    <div className="flex gap-6 border-b border-gray-100 px-6 py-3 text-sm">
                        <span className="text-gray-500">{buyers.length} customer{buyers.length !== 1 ? 's' : ''}</span>
                        <span className="text-gray-500">Revenue 12m: <span className="font-medium text-gray-700">{formatEUR(totalRevenue12m)} EUR</span></span>
                        <span className="text-gray-500">All time: <span className="font-medium text-gray-700">{formatEUR(totalRevenueAllTime)} EUR</span></span>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-auto pb-4">
                    {loading && (
                        <div className="flex items-center justify-center py-16">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}

                    {error && (
                        <div className="m-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {!loading && !error && buyers.length === 0 && (
                        <div className="py-16 text-center text-sm text-gray-500">
                            No buyers found for this product.
                        </div>
                    )}

                    {!loading && !error && buyers.length > 0 && (
                        <BuyerTable buyers={buyers} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                    )}

                    {/* Similar Products in Other Companies */}
                    {!loading && !error && similarProducts.length > 0 && (
                        <div className="mt-6">
                            <div className="mx-6 mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                                <div>
                                    <p className="text-sm font-medium text-amber-800">Similar Products in Other Companies</p>
                                    <p className="mt-0.5 text-xs text-amber-700">
                                        Based on product name similarity — not an exact match. Verify product details before comparing.
                                    </p>
                                </div>
                            </div>

                            {similarProducts.map(sp => (
                                <div key={`${sp.productId}__${sp.companyCode}`} className="mt-4">
                                    <div className="mx-6 mb-2 flex items-baseline gap-2">
                                        <span className="text-sm font-medium text-gray-800">{sp.productName}</span>
                                        <span className="text-xs text-gray-400">({sp.productId.split('-').slice(1).join('-')})</span>
                                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">{sp.companyCode}</span>
                                        <span className="text-xs text-gray-400">{sp.buyers.length} customer{sp.buyers.length !== 1 ? 's' : ''}</span>
                                    </div>
                                    <BuyerTable buyers={sp.buyers} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
